/**
 * Computes which items are new or modified between two version snapshots. Used by both the Compare
 * page and inline diff badges in the DTA Editor.
 */

import type { DTADocumentContent } from "../types/dta"

export type DiffStatus = "new" | "modified"

export interface InlineDiffResult {
  schema: Map<string, DiffStatus>
  codelists: Map<string, DiffStatus>
  dqRules: Map<string, DiffStatus>
  testConcepts: Map<string, DiffStatus>
}

type DocContent = Record<string, unknown>

export function computeInlineDiff(
  prevContent: Partial<DTADocumentContent> | undefined,
  currentContent: Partial<DTADocumentContent> | undefined,
): InlineDiffResult {
  const prev = prevContent || {}
  const curr = currentContent || {}

  return {
    schema: diffByKey(
      ((prev.transfer_metadata_schema as Record<string, unknown>)?.variables || []) as object[],
      ((curr.transfer_metadata_schema as Record<string, unknown>)?.variables || []) as object[],
      "variable_name",
    ),
    codelists: diffByKey(prev.code_lists || [], curr.code_lists || [], "name", "id"),
    dqRules: diffRules(
      prev.data_quality_rules as DocContent,
      curr.data_quality_rules as DocContent,
    ),
    testConcepts: diffByKey(prev.test_concepts || [], curr.test_concepts || [], "id"),
  }
}

function diffByKey<T extends object>(
  oldArr: T[],
  newArr: T[],
  keyField: string,
  fallbackKey?: string,
): Map<string, DiffStatus> {
  const result = new Map<string, DiffStatus>()
  const getKey = (item: T): string => {
    const val =
      (item as Record<string, unknown>)[keyField] ??
      (item as Record<string, unknown>)[fallbackKey ?? ""] ??
      ""
    return typeof val === "string" ? val : JSON.stringify(val)
  }
  const oldMap = new Map(oldArr.filter((i) => getKey(i)).map((i) => [getKey(i), i]))

  for (const item of newArr) {
    const key = getKey(item)
    if (!key) continue
    if (!oldMap.has(key)) {
      result.set(key, "new")
    } else if (!deepEqual(oldMap.get(key)!, item)) {
      result.set(key, "modified")
    }
  }
  return result
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== "object" || typeof b !== "object") return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, i) => deepEqual(item, (b as unknown[])[i]))
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const aObj = a as Record<string, unknown>
  const bObj = b as Record<string, unknown>
  const aKeys = Object.keys(aObj).sort()
  const bKeys = Object.keys(bObj).sort()
  if (aKeys.length !== bKeys.length) return false
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return false
    if (!deepEqual(aObj[aKeys[i]], bObj[bKeys[i]])) return false
  }
  return true
}

function diffRules(
  oldRules: DocContent | undefined,
  newRules: DocContent | undefined,
): Map<string, DiffStatus> {
  const result = new Map<string, DiffStatus>()
  const toKey = (r: DocContent) => `${String(r.type)}:${String(r.column)}`
  const oldAll = [
    ...((oldRules?.majorRules as DocContent[]) || []),
    ...((oldRules?.minorRules as DocContent[]) || []),
  ]
  const newAll = [
    ...((newRules?.majorRules as DocContent[]) || []),
    ...((newRules?.minorRules as DocContent[]) || []),
  ]
  const oldMap = new Map(oldAll.filter((r) => r.type && r.column).map((r) => [toKey(r), r]))

  for (const rule of newAll) {
    const key = toKey(rule)
    if (!key || key === "undefined:undefined") continue
    if (!oldMap.has(key)) {
      result.set(key, "new")
    } else if (!deepEqual(oldMap.get(key)!, rule)) {
      result.set(key, "modified")
    }
  }
  return result
}
